import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { GitlabServicesMtsRuMtsMediaCorporateCorporateBackendProjectsProject as Projects } from 'api/models'
import { CatalogItemType } from 'models'
import { Button, CatalogItemLabel, IconBottom } from 'shared/components'
import { delay } from 'lodash'
import { transformGenre } from 'shared/pipes'
import { pluralize } from 'shared/utils'
import classes from './CatalogFullItem.module.scss'

type Props = {
  item: Projects
  active: boolean
  onClick: () => void
}

const middleDotHtml = <>&#183;</>

export const CatalogFullItem = ({
  item,
  onClick,
  active,
}: Props): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null)
  const [isTextOpened, setTextOpened] = useState(false)

  useEffect(() => {
    const el = ref.current
    setTextOpened(false)

    if (active && el) {
      delay(() => {
        window.scrollTo({
          top: el.getBoundingClientRect()?.top + window.scrollY - 85,
          behavior: 'smooth',
        })
      }, 400)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, item.uid])

  const renderRateItem = (text: ReactNode, withMiddleDot?: boolean) => {
    return (
      <>
        <div className={classes.rate_item}>{text}</div>
        {withMiddleDot && middleDotHtml}
      </>
    )
  }

  const renderRate = (title: string, rate: number) => {
    return renderRateItem(
      <>
        <span> {title} </span> {rate}
      </>
    )
  }

  const renderSeasons = () => {
    const hasSeasons = [
      CatalogItemType.Serial,
      CatalogItemType.Show,
      CatalogItemType.KinoStories,
    ]

    if (!hasSeasons.includes(item.type)) {
      return <></>
    }

    return (
      <>
        {renderRateItem(`Cезон ${item?.numberOfSeasons}`, true)}
        {renderRateItem(
          `${item?.numberOfEpisodes} ${pluralize(item?.numberOfEpisodes, [
            'серия',
            'серии',
            'серий',
          ])}`,
          true
        )}
      </>
    )
  }

  const renderInfo = (title: string, value: string, cls?: string) => (
    <div className={`${classes.info_item} ${cls || ''}`}>
      <div className={classes.title}>{title}</div>
      {value}
    </div>
  )

  const bg = !item?.name
    ? ``
    : `linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(${item?.projectImage?.image?.href})`

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderGenre = useMemo(
    () => (item?.genre || []).map((g: number) => transformGenre(g)).join(', '),
    [item?.genre]
  )

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderCountry = useMemo(
    () => (item?.countries || []).join(', '),
    [item?.countries]
  )

  const renderDescription = useMemo((): JSX.Element => {
    const text = item?.content || ''

    if (!text.length) {
      return <></>
    }

    // если длина больше 444 символов то добавляется элемент "Развернуть"
    const maxLength = 444

    const renderText =
      text.length >= maxLength
        ? [text.slice(0, maxLength), text.slice(maxLength)]
        : [text, '']

    const fullText =
      renderText[1].length > 0 ? (
        <>
          {isTextOpened && renderText[1]}
          <IconBottom
            openText={'Развернуть'}
            closeText={'Свернуть'}
            isOpen={isTextOpened}
            onClick={() => setTextOpened(!isTextOpened)}
            className={`link color ${classes.icon}`}
            withTransition={false}
          ></IconBottom>
        </>
      ) : null

    return (
      <>
        {renderText[0]}
        {fullText}
      </>
    )
  }, [item?.content, isTextOpened])

  return (
    <div
      ref={ref}
      className={classes.item}
      style={{
        backgroundImage: bg,
      }}
    >
      <i className={`${classes.close} icon-close`} onClick={onClick}></i>
      <div className={classes.item_container}>
        <div className={classes.labels}>
          {[1, 2].includes(item.specialCategory) && (
            <CatalogItemLabel
              isPremiereOrLeader={item.specialCategory}
              type={item.type}
            />
          )}
          <CatalogItemLabel type={item.type} />
        </div>
        <h1 className={`title ${classes.title}`}>{item.name}</h1>
        <div className={classes.rate}>
          {renderSeasons()}
          {renderRate('KION', item.assessment)}
          {renderRate('IMDB', item.assessmentImdb)}
          {renderRate('КП', item.cinemaSearchRating)}
        </div>
        <div className={classes.description}>{renderDescription}</div>
        <div className={classes.info}>
          <div className={classes.info_wrap}>
            {renderInfo('ЖАНР', renderGenre, classes.info_item_genre)}
            {renderInfo('СТРАНА', renderCountry)}
            {renderInfo('ГОД', item?.premiere)}
          </div>
          <div className={classes.btn_wrap}>
            <a href={item?.refMtsTv?.href} target="_blank" rel="noreferrer">
              <Button className={classes.btn}>Смотреть на KION</Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
